import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  MinusIcon,
  CircleStackIcon,
  ComputerDesktopIcon,
  FingerPrintIcon,
} from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import Tooltip from '../UI/Tooltip';
import { userAppContext } from '../../context/Context';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import { useCheckoutFlow } from '../../hooks/useCheckoutFlow';
import { UserContext } from '../../types';
import './pricingtable.css';
import pricingData from './pricingData.json';
import useScrollWindow from '@/hooks/useScrollWindow';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { GoInfo } from 'react-icons/go';
import { usePricingStats, ACADEMIC_FORMATTED, type FormattedPricingStats } from './usePricingStats';
import { filterPricingRows, isPremiumColumnVisible } from './pricingVisibility';

const ROW_LABEL_TO_STATS_KEY: Record<string, keyof FormattedPricingStats> = {
  Genes: 'gene',
  Variants: 'variant',
  'Diseases & Phenotypes': 'disease',
  'Gene-Disease Associations (GDAs)': 'gda_summary',
  'Variant-Disease Associations (VDAs)': 'vda_summary',
  'Disease-Disease Associations (DDAs)': 'dda',
  'Drugs & chemicals': 'chemical',
};

interface PricingTablesProps {
  activeTab: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onAcademicApply: () => void;
  showColumnPrices?: boolean;
  columnIds?: string[];
}

function PricingTables({
  activeTab,
  isExpanded,
  onToggleExpand,
  onAcademicApply,
  showColumnPrices = false,
  columnIds,
}: PricingTablesProps) {
  const navigate = useNavigate();
  const { groups, user, isAuthenticated } = userAppContext() as unknown as UserContext;
  const {
    trialType,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email, isAuthenticated);
  const { redirectToCheckout } = useCheckoutFlow();
  const { isMobile, isTablet } = useScrollWindow();
  const isDesktop = !isMobile && !isTablet;
  const { formatted: pricingStats } = usePricingStats();

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademicPlan = groups?.includes('ROLE_ACADEMIC');
  const isStandardPlan = groups?.includes('ROLE_STANDARD');
  const isAdvancedPlan = groups?.includes('ROLE_PROFESSIONAL');
  const canSeePrices = canSeePricesFromHook();
  const shouldShowContactUs = isAuthenticated && (isStandardPlan || isAcademicPlan) && !canSeePrices;
  const isCommercial = isTrial ? trialType === 'commercial' : isCommercialFromHook() || shouldShowContactUs;

  const canShowPrices =
    showColumnPrices && isAuthenticated && !isCommercial && !isTrial && canSeePrices && !shouldShowContactUs;

  // Map col.id to plan title for reusing PricingCard logic
  const colIdToTitle: Record<string, string> = {
    freeAcademic: 'Academic',
    standard: 'Standard',
    advanced: 'Advanced',
    premium: 'Premium',
  };
  const [openSections, setOpenSections] = useState<Set<number>>(
    new Set(pricingData.sections.map((_, i) => i)),
  );

  const renderIcon = (icon: string) => {
    if (icon === 'circle-stack') return <CircleStackIcon className="w-5 h-5 flex-shrink-0" />;
    if (icon === 'computer-desktop') return <ComputerDesktopIcon className="w-5 h-5 flex-shrink-0" />;
    if (icon === 'fingerpring') return <FingerPrintIcon className="w-5 h-5 flex-shrink-0" />;
    return null;
  };

  const renderTabTitleButton = (col: any) => {
    const title = colIdToTitle[col.id];
    const isAdvancedCard = title === 'Advanced';
    const variant = isAdvancedCard ? 'primary' : 'secondaryOutline';

    const isCurrentPlan =
      (title === 'Academic' && isAcademicPlan) ||
      (title === 'Standard' && isStandardPlan) ||
      (title === 'Advanced' && isAdvancedPlan);

    if (!isAuthenticated) {
      if (title === 'Academic') {
        return (
          <SharedButton
            variant={variant}
            size="xs"
            className="my-0"
            onClick={() => navigate('/academic-apply')}
          >
            Apply for Free Academic Plan
          </SharedButton>
        );
      }
      if (title === 'Advanced') {
        return (
          <SharedButton
            variant={variant}
            size="xs"
            className="my-0"
            onClick={() => navigate('/free-trial-apply')}
          >
            Try it out
          </SharedButton>
        );
      }
      if (title === 'Academic') {
        return (
          <SharedButton
            variant={variant}
            size="xs"
            className="my-0"
            onClick={() => navigate('/free-trial-apply')}
          >
            Apply for Free Academic Plan
          </SharedButton>
        );
      }
      return (
        <SharedButton variant={variant} size="xs" className="my-0" onClick={() => navigate('/Support')}>
          Contact Us
        </SharedButton>
      );
    }

    if (isCurrentPlan) {
      return (
        <SharedButton
          variant={variant}
          size="xs"
          className="my-0"
          disabled
          style={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          Current Plan
        </SharedButton>
      );
    }

    if (shouldShowContactUs || isCommercial) {
      return (
        <SharedButton variant={variant} size="xs" className="my-0" onClick={() => navigate('/Support')}>
          Contact Us
        </SharedButton>
      );
    }

    if (isTrial) {
      if (title === 'Academic') {
        return (
          <SharedButton variant={variant} size="xs" className="my-0" onClick={onAcademicApply}>
            Apply for Free Academic Plan
          </SharedButton>
        );
      }
      if (title === 'Standard' || title === 'Advanced') {
        const priceId =
          title === 'Standard'
            ? process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID || 'price_1SCebx2Ml6stFA0rHcXCPwNj'
            : process.env.REACT_APP_STRIPE_ADVANCED_PRICE_ID || 'price_1SPPcK2Ml6stFA0rOiXzsUUM';
        return (
          <Tooltip
            content="Apply for the Free Academic Plan to access paid plans"
            placement="top"
            allowCopy={false}
          >
            <SharedButton
              variant={variant}
              size="xs"
              className="my-0"
              disabled
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
              onClick={() => redirectToCheckout({ title, priceId })}
            >
              Get {title}
            </SharedButton>
          </Tooltip>
        );
      }
      return (
        <SharedButton variant={variant} size="xs" className="my-0" onClick={() => navigate('/Support')}>
          Contact Us
        </SharedButton>
      );
    }

    if (isAcademicPlan && (title === 'Standard' || title === 'Advanced')) {
      const priceId =
        title === 'Standard'
          ? process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID || 'price_1SCebx2Ml6stFA0rHcXCPwNj'
          : process.env.REACT_APP_STRIPE_ADVANCED_PRICE_ID || 'price_1SPPcK2Ml6stFA0rOiXzsUUM';
      return (
        <SharedButton
          variant={variant}
          size="xs"
          className="my-0"
          onClick={() => redirectToCheckout({ title, priceId })}
        >
          Get {title}
        </SharedButton>
      );
    }

    if (isStandardPlan && title === 'Advanced') {
      const priceId = process.env.REACT_APP_STRIPE_ADVANCED_PRICE_ID || 'price_1SPPcK2Ml6stFA0rOiXzsUUM';
      return (
        <SharedButton
          variant={variant}
          size="xs"
          className="my-0"
          onClick={() => redirectToCheckout({ title, priceId })}
        >
          Get Advanced
        </SharedButton>
      );
    }

    if (isAdvancedPlan && title === 'Standard') {
      const priceId = process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID || 'price_1SCebx2Ml6stFA0rHcXCPwNj';
      return (
        <SharedButton
          variant={variant}
          size="xs"
          className="my-0"
          onClick={() => redirectToCheckout({ title, priceId })}
        >
          Get Standard
        </SharedButton>
      );
    }

    if (title === 'Premium') {
      return (
        <SharedButton variant={variant} size="xs" className="my-0" onClick={() => navigate('/Support')}>
          Contact Us
        </SharedButton>
      );
    }

    if (title === 'Academic') {
      return (
        <SharedButton
          variant={variant}
          size="xs"
          className="my-0"
          onClick={() => navigate('/academic-apply')}
        >
          Apply for Free Academic Plan
        </SharedButton>
      );
    }

    return (
      <SharedButton
        variant={variant}
        size="xs"
        className="my-0"
        onClick={() => navigate('/free-trial-apply')}
      >
        Try it out
      </SharedButton>
    );
  };

  const visibleColumns = pricingData.columns.filter(col => {
    if (columnIds) return columnIds.includes(col.id);
    if (activeTab === 'academic') return col.showForAcademic;
    if (activeTab === 'business') return col.showForCommercial;
    return true;
  });

  const showOfflineAccess = isPremiumColumnVisible(visibleColumns.map(col => col.id));

  const toggleSection = (idx: number) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const labelWidth = 40;
  const colWidth = (100 - labelWidth) / visibleColumns.length;

  // Shared colgroup to keep column widths aligned across all tables
  const sharedColGroup = (
    <colgroup>
      <col style={{ width: `${labelWidth}%` }} />
      {visibleColumns.map(col => (
        <col key={col.id} style={{ width: `${colWidth}%` }} />
      ))}
    </colgroup>
  );

  const renderCellContent = (cellData: unknown, row?: { label?: string }, colId?: string) => {
    if (!cellData && !row) return null;

    const statsKey = row?.label ? ROW_LABEL_TO_STATS_KEY[row.label] : undefined;
    if (statsKey && colId) {
      const stats =
        colId === 'freeAcademic' ? ACADEMIC_FORMATTED[statsKey] : pricingStats?.[statsKey];
      if (stats && stats !== '0') return stats;
    }

    if (!cellData || typeof cellData !== 'object') return null;
    const data = cellData as { type?: string; value?: string };
    if (data.type === 'icon') {
      if (data.value === 'x-circle') {
        return <MinusIcon className="w-5 h-5 mx-auto" style={{ strokeWidth: 2.5 }} />;
      }
      if (data.value === 'check-green') {
        return <CheckIcon className="w-5 h-5 mx-auto" style={{ strokeWidth: 2.5 }} />;
      }
      if (data.value === 'check-yellow') {
        return <span>Expert-curated data</span>;
      }
    }

    return data.value;
  };

  const renderLabel = row => {
    if (row.link) {
      return (
        <a href={row.link} target="_blank" rel="noopener noreferrer">
          <u className="table-subsubtitles text-sm font-medium" style={{ color: '#52B7DB' }}>
            {row.label}
          </u>
        </a>
      );
    }

    return (
      <span className="table-subsubtitles text-sm font-medium flex items-center">
        {row.label}
        {row.tooltip && (
          <div className="ml-1 pt-1">
            <BaseTooltip
              content={row.tooltip}
              style={{ whiteSpace: 'break-spaces', width: '15em' }}
              position="right"
            >
              <GoInfo className="w-5 h-4 stroke-1 text-blue m-1" />
            </BaseTooltip>
          </div>
        )}
      </span>
    );
  };

  const PREVIEW_ROW_COUNT = 6;

  const renderSectionTable = (rows: (typeof pricingData.sections)[0]['rows']) => (
    <table
      className="table border-collapse border border-gray-300 mb-4 bg-white"
      style={{ tableLayout: 'fixed', width: '100%', margin: 0, textAlign: 'center' }}
    >
      {sharedColGroup}
      <tbody>
        {filterPricingRows(rows, showOfflineAccess).map((row, rowIdx) => (
          <tr key={rowIdx}>
            <td className="text-left font-medium text-sm">{renderLabel(row)}</td>
            {visibleColumns.map(col => (
              <td key={col.id}>{renderCellContent(row[col.id], row, col.id)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div id="pricingTableSection">
      <div style={{ position: 'relative' }}>
        <div
          className="pricing-collapse"
          style={{
            maxHeight: isExpanded ? 'none' : undefined,
            overflow: isExpanded ? 'visible' : 'hidden',
            transition: 'max-height 0.4s ease',
          }}
        >
          <div className="pricing-table-inner">
            {/* Shared plan header — shown once at the top */}
            <table
              className="table"
              style={{
                tableLayout: 'fixed',
                width: '100%',
                margin: '10px auto 0',
                textAlign: 'center',
                borderCollapse: 'separate',
                borderSpacing: '4px 0',
              }}
            >
              {sharedColGroup}
              {isDesktop && (
                <thead>
                  <tr className="table-titles">
                    <th style={{ verticalAlign: 'middle' }} className="text-left">
                      <span className="text-xl">Compare all plans</span>
                    </th>
                    {visibleColumns.map(col => (
                      <th key={col.id} className={`${col.label === 'ADVANCED' ? 'tab-title-advanced' : ''}`}>
                        <div className="flex items-center gap-2 flex-col">
                          <span className="text-lg">{col.label}</span>
                          {canShowPrices && col.price && (
                            <div className="flex flex-col items-center leading-tight">
                              {col.priceMo ? (
                                <>
                                  <span className="font-bold text-base">
                                    {col.priceMo}
                                    <small>€/mo</small>
                                  </span>
                                  <small className="opacity-70">({col.price}€/year)</small>
                                </>
                              ) : (
                                <span className="font-bold text-base">{col.price}</span>
                              )}
                            </div>
                          )}
                          {renderTabTitleButton(col)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
            </table>

            {/* Each section as its own independent table inside an accordion */}
            {(isExpanded ? pricingData.sections : pricingData.sections.slice(0, 1)).map(
              (section, sectionIdx) => {
                const isOpen = openSections.has(sectionIdx);

                if (!isExpanded) {
                  return (
                    <div key={sectionIdx} className="pricing-section-accordion mt-3">
                      <div className="pricing-section-toggle flex items-center gap-2">
                        {renderIcon(section.icon)}
                        <span className="text-lg font-medium">{section.title}</span>
                      </div>
                      {renderSectionTable(section.rows.slice(0, PREVIEW_ROW_COUNT))}
                    </div>
                  );
                }

                return (
                  <div key={sectionIdx} className="pricing-section-accordion mt-3">
                    {/* Accordion header */}
                    <button
                      onClick={() => toggleSection(sectionIdx)}
                      className="pricing-section-toggle flex items-center gap-2 justify-between"
                    >
                      <div className="flex items-center gap-2">
                        {renderIcon(section.icon)}
                        <span className="text-lg font-medium">{section.title}</span>
                      </div>
                      {isOpen ? (
                        <ChevronUpIcon className="w-4 h-4 flex-shrink-0" style={{ strokeWidth: 3 }} />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 flex-shrink-0" style={{ strokeWidth: 3 }} />
                      )}
                    </button>

                    {/* Section table */}
                    {isOpen && renderSectionTable(section.rows)}
                  </div>
                );
              },
            )}
          </div>
        </div>

        {!isExpanded && (
          <div
            className="pricing-table-fade"
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '80px',
              background: 'linear-gradient(to bottom, transparent, white)',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      <div className="pricing-table-toggle-btn flex justify-center mt-3 mb-10">
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-2 text-sm font-semibold"
          style={{ color: 'var(--blue-color)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {isExpanded ? (
            <>
              Show less <ChevronUpIcon className="w-4 h-4" />
            </>
          ) : (
            <>
              Show full comparison <ChevronDownIcon className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default PricingTables;
